import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _, { keyBy } from 'lodash';
import { availabilityReservationTemplatesSelector } from 'slices/reservationTemplate.slice';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import type { DrawerPayloadAction } from 'types/utils.type';
import { getAllData } from 'utils/fetchAllData';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  type TReservationMode,
  createEmptyReservationMode,
} from '@timeedit/types';
import api from '../services/api.service';

const TEMP_ID = 0;

type TReservationModeState = IDefaultReducerState & {
  selectedReservationMode: TReservationMode;
  allReservationModes: TReservationMode[];
  reservationModesForOtherPages: TReservationMode[];
  map: Record<number, TReservationMode>;
  firstDrawer?: TReservationMode;
  secondDrawer?: TReservationMode;
};

export const initialState: TReservationModeState = {
  loading: false,
  hasErrors: false,
  selectedReservationMode: createEmptyReservationMode(),
  allReservationModes: [],
  reservationModesForOtherPages: [],
  map: {},
  firstDrawer: undefined,
  secondDrawer: undefined,
};

// Slice
const slice = createSlice({
  name: 'reservationModes',
  initialState,
  reducers: {
    fetchReservationModesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchReservationModesSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TReservationMode>>
    ) => {
      state.allReservationModes = payload.results;
      state.map = keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchReservationModesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createReservationModeRequest: (state) => {
      isLoadingRequest(state);
    },
    createReservationModeSuccess: (
      state,
      { payload }: PayloadAction<TReservationMode>
    ) => {
      if (!state.map[TEMP_ID]) {
        state.allReservationModes.unshift(payload);
        state.map[payload.id] = payload;
      }
      finishedLoadingSuccess(state);
    },
    createReservationModeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteReservationModeRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteReservationModeSuccess: (
      state,
      { payload: reservationModeId }: PayloadAction<number>
    ) => {
      state.allReservationModes = state.allReservationModes.filter(
        (mode: TReservationMode) => mode.id !== reservationModeId
      );
      state.selectedReservationMode = createEmptyReservationMode();
      finishedLoadingSuccess(state);
    },
    deleteReservationModeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateReservationModeRequest: (state) => {
      isLoadingRequest(state);
    },
    updateReservationModeSuccess: (
      state,
      { payload }: PayloadAction<TReservationMode>
    ) => {
      const newReservationModes = [...state.allReservationModes];
      const index = state.allReservationModes.findIndex(
        (mode: TReservationMode) => mode.id === payload.id
      );
      newReservationModes[index] = payload;
      state.allReservationModes = newReservationModes;
      state.selectedReservationMode = payload;
      finishedLoadingSuccess(state);
    },
    updateReservationModeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstDrawer: (state, { payload }: PayloadAction<TReservationMode>) => {
      const firstDrawer = state.firstDrawer;
      state.firstDrawer = payload;
      if (!payload && firstDrawer?.id === TEMP_ID) {
        state.allReservationModes = state?.allReservationModes?.filter(
          (item: TReservationMode) => item.id !== TEMP_ID
        );
      }
    },
    setSecondDrawer: (state, { payload }: PayloadAction<TReservationMode>) => {
      state.secondDrawer = payload;
    },
    updateDrawer: <Key extends keyof TReservationMode>(
      state: TReservationModeState,
      { payload }: DrawerPayloadAction<TReservationMode, Key>
    ) => {
      const { isFirstDrawer, key, value } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = {
          ...state.firstDrawer,
          [key]: value,
        };
      } else {
        state.secondDrawer = {
          ...state.secondDrawer,
          [key]: value,
        };
      }
    },
    duplicateReservationModes: (
      state,
      { payload }: PayloadAction<TReservationMode>
    ) => {
      const newItem = {
        ...payload,
        id: TEMP_ID,
        name: payload.name + '-copy',
        history: [],
      };

      state.allReservationModes = state.allReservationModes.filter(
        (reservationMode) => reservationMode.id !== TEMP_ID
      );
      state.firstDrawer = newItem;
      state.allReservationModes.unshift(newItem);
      state.map[TEMP_ID] = newItem;
    },
    discardChanges: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: number;
        isFirstDrawer: boolean;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = state.map[id];
      } else {
        state.secondDrawer = state.map[id];
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const reservationModesLoading = (state: TRootState) =>
  state.reservationModes.loading;
export const reservationModesSelector = (state: TRootState) =>
  state.reservationModes.allReservationModes;
export const reservationModesMapSelector = (state: TRootState) =>
  state.reservationModes.map;
export const selectedFirstDrawerSelector = (state: TRootState) =>
  state.reservationModes.firstDrawer;
export const selectedSecondDrawerSelector = (state: TRootState) =>
  state.reservationModes.secondDrawer;

export const selectedIdsSelector = createSelector(
  [
    (state: TRootState) => state.reservationModes.firstDrawer,
    (state: TRootState) => state.reservationModes.secondDrawer,
  ],
  (firstDrawer, secondDrawer) =>
    _.compact([firstDrawer, secondDrawer]).map((pr) => pr.id)
);

export const availabilityReservationModesSelector = createSelector(
  reservationModesSelector,
  availabilityReservationTemplatesSelector,
  (reservationModes, reservationTemplates) => {
    const resTempAvailabilityIds = new Set(
      reservationTemplates.map((res) => res.id)
    );
    return reservationModes.filter((res) =>
      res.reservationTemplates.some((tempId) =>
        resTempAvailabilityIds.has(tempId)
      )
    );
  }
);

// Actions
export const {
  fetchReservationModesRequest,
  fetchReservationModesSuccess,
  fetchReservationModesFailure,
  createReservationModeRequest,
  createReservationModeSuccess,
  createReservationModeFailure,
  deleteReservationModeRequest,
  deleteReservationModeSuccess,
  deleteReservationModeFailure,
  updateReservationModeRequest,
  updateReservationModeSuccess,
  updateReservationModeFailure,
  setFirstDrawer,
  setSecondDrawer,
  updateDrawer,
  duplicateReservationModes,
  discardChanges,
} = slice.actions;

type TFetchReservationModes = {
  sort?: string;
  templateType?: string;
};

export const fetchReservationModes = createAppAsyncThunk<
  void,
  void | TFetchReservationModes
>('reservationModes/fetchReservationModes', async (payload, { dispatch }) => {
  const { sort, templateType } = payload || {};
  try {
    const params = {
      ...(sort && { sort }),
      ...(templateType && { templateType }),
    };

    dispatch(fetchReservationModesRequest());
    const reservationModes = await getAllData(
      {
        endpoint: `/reservation-modes?${new URLSearchParams(params)}`,
      },
      'get'
    );
    dispatch(fetchReservationModesSuccess(reservationModes));
  } catch (e) {
    dispatch(fetchReservationModesFailure());
    return console.error(e);
  }
});

export const createReservationMode = createAppAsyncThunk<
  void,
  TReservationMode
>(
  'reservationModes/createReservationMode',
  async (reservationMode, { dispatch }) => {
    try {
      dispatch(createReservationModeRequest());
      const { id, history, ...reservationModeBody } = reservationMode;
      const response = await api.post({
        endpoint: `/reservation-modes`,
        data: reservationModeBody,
      });
      dispatch(createReservationModeSuccess(response));
      dispatch(fetchReservationModes());
      dispatch(setFirstDrawer(undefined));
    } catch (e: any) {
      dispatch(createReservationModeFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const createUnsavedReservationMode = (): TAppThunk => (dispatch) => {
  const response = createEmptyReservationMode();
  dispatch(createReservationModeSuccess(response));
  dispatch(setFirstDrawer(response));
};

type TDeleteReservationModePayload = {
  reservationModeId: number;
  isFirstDrawer?: boolean;
};

export const deleteReservationMode = createAppAsyncThunk<
  void,
  TDeleteReservationModePayload
>(
  'reservationModes/deleteReservationMode',
  async ({ isFirstDrawer, reservationModeId }, { dispatch }) => {
    try {
      dispatch(deleteReservationModeRequest());
      await api.delete({
        endpoint: `/reservation-modes/${reservationModeId}`,
      });
      if (isFirstDrawer === true) {
        dispatch(setFirstDrawer(undefined));
      } else if (isFirstDrawer === false) {
        dispatch(setSecondDrawer(undefined));
      }
      dispatch(deleteReservationModeSuccess(reservationModeId));
    } catch (e: any) {
      dispatch(deleteReservationModeFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

type TUpdateReservationModePayload = {
  reservationMode: TReservationMode;
  isFirstDrawer: boolean;
};

export const updateReservationMode = createAppAsyncThunk<
  void,
  TUpdateReservationModePayload
>(
  'reservationModes/updateReservationMode',
  async ({ isFirstDrawer, reservationMode }, { dispatch }) => {
    try {
      dispatch(updateReservationModeRequest());
      const { id, history, ...reservationModeBody } = reservationMode;
      const response = await api.patch({
        endpoint: `/reservation-modes/${reservationMode.id}`,
        data: reservationModeBody,
      });
      dispatch(updateReservationModeSuccess(response));
      dispatch(fetchReservationModes());
      if (isFirstDrawer) {
        dispatch(setFirstDrawer(undefined));
      } else {
        dispatch(setSecondDrawer(undefined));
      }
    } catch (e: any) {
      dispatch(updateReservationModeFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const getDrawer =
  (isFirstDrawer: boolean): TAppThunk<TReservationMode> =>
  (_, getState) => {
    const { reservationModes } = getState();
    const { firstDrawer, secondDrawer } = reservationModes;
    return isFirstDrawer ? firstDrawer : secondDrawer;
  };

export const checkDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (dispatch, getState) => {
    try {
      const { reservationModes } = getState();
      const { firstDrawer, secondDrawer, map } = reservationModes;
      if (isFirstDrawer && firstDrawer && map[firstDrawer.id]) {
        return checkSavedDisabled(firstDrawer, map[firstDrawer.id]);
      }
      if (!isFirstDrawer && secondDrawer && map[secondDrawer.id]) {
        return checkSavedDisabled(secondDrawer, map[secondDrawer.id]);
      }

      return true;
    } catch (e) {
      console.error(e);
    }
  };
